import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, Plus, ChevronRight } from "react-feather";
import {
  selectAllPlannedMeals,
  selectAllRecipes,
  useAppSelector,
  MEAL_SLOTS,
  MEAL_SLOT_LABEL_KEYS,
  useT,
  type MealSlot,
} from "@reciperoll/core";
import { RecipeImage } from "../RecipeImage";

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Today's planned meals, or a prompt to plan the day when empty. Mirrors RN.
export default function TodaysMealPlan() {
  const t = useT();
  const allPlanned = useAppSelector(selectAllPlannedMeals);
  const recipes = useAppSelector(selectAllRecipes);
  const recipesById = useMemo(() => new Map(recipes.map((r) => [r.id, r])), [recipes]);
  const today = ymd(new Date());

  const bySlot = useMemo(() => {
    const map: Partial<Record<MealSlot, typeof allPlanned>> = {};
    for (const m of allPlanned) {
      if (m.mealDate !== today) continue;
      if (!recipesById.has(m.recipeId)) continue; // recipe was deleted
      (map[m.slot] ??= []).push(m);
    }
    for (const list of Object.values(map)) list?.sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [allPlanned, today, recipesById]);

  const slotsWithMeals = MEAL_SLOTS.filter((s) => (bySlot[s]?.length ?? 0) > 0);
  const total = slotsWithMeals.reduce((n, s) => n + (bySlot[s]?.length ?? 0), 0);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-terracotta-ink" />
          <h2 className="font-display text-2xl font-semibold tracking-tight text-ink">
            {t("mealPlan.todaysMeals")}
          </h2>
        </div>
        <Link
          to="/meal-plan"
          className="shrink-0 text-sm font-semibold text-terracotta-ink hover:text-terracotta-dark"
        >
          {t("nav.mealPlanner")}
        </Link>
      </div>

      {total === 0 ? (
        <Link
          to="/meal-plan"
          className="flex flex-col items-center text-center bg-surface rounded-2xl border border-line p-6 hover:bg-surface-2 transition-colors"
        >
          <div className="w-12 h-12 rounded-full bg-terracotta-wash flex items-center justify-center mb-2">
            <Calendar className="w-5 h-5 text-terracotta-ink" />
          </div>
          <p className="text-base font-bold text-ink">{t("mealPlan.nothingPlannedToday")}</p>
          <p className="text-sm text-ink-soft mt-0.5">
            {t("mealPlan.planPrompt")}
          </p>
          <span className="inline-flex items-center gap-1.5 mt-3 bg-terracotta text-white rounded-xl px-4 py-2.5 text-sm font-bold">
            <Plus className="w-4 h-4" /> {t("mealPlan.planToday")}
          </span>
        </Link>
      ) : (
        <div className="bg-surface rounded-2xl border border-line overflow-hidden">
          {slotsWithMeals.map((slot, i) => (
            <div key={slot} className={`px-4 py-2.5 ${i > 0 ? "border-t border-line" : ""}`}>
              {/* Slot label as a header above the group, so it reads correctly
                  whether the slot holds one meal or several. */}
              <p className="text-[11px] font-bold uppercase tracking-wider text-terracotta-ink mb-2">
                {t(MEAL_SLOT_LABEL_KEYS[slot])}
              </p>
              {bySlot[slot]!.map((m, j) => {
                const recipe = recipesById.get(m.recipeId)!;
                return (
                  <Link
                    key={m.id}
                    to={`/recipe/${recipe.id}`}
                    className={`flex items-center group ${j > 0 ? "mt-3" : ""}`}
                  >
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-terracotta-wash shrink-0">
                      <RecipeImage recipe={recipe} />
                    </div>
                    <div className="flex-1 min-w-0 ml-3">
                      <p className="text-base font-semibold text-ink truncate group-hover:text-terracotta-ink transition-colors">
                        {recipe.title}
                      </p>
                    </div>
                    <ChevronRight className="w-[18px] h-[18px] text-ink-faint shrink-0" />
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
